import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ORGANIZATION_DISPLAY_NAME } from '../config/branding';

/**
 * Connexion « classique » Entrea — panneau visuel + formulaire (réservée aux accès
 * type administrateur / console : même API IAM, autre présentation).
 */
const LoginAdmin = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);

    if (result.success) {
      if (result.mustChangePassword) {
        navigate('/first-login-password', { state: { returnFallback: '/portal' } });
      } else {
        navigate('/portal');
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-white lg:bg-gray-50">
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 opacity-90" />
        <img
          src="https://images.unsplash.com/photo-1576267423048-15c0040fec78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbGxhYm9yYXRpb24lMjB0ZWFtd29ya3xlbnwwfHx8fDE3NjUzOTY4Nzl8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/80 via-purple-600/80 to-pink-600/80" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <p className="text-sm font-semibold uppercase tracking-wider text-indigo-100 mb-2">Accès administrateur</p>
          <h1 className="text-5xl font-bold mb-6 leading-tight">
            Bienvenue à<br />
            {ORGANIZATION_DISPLAY_NAME}
          </h1>
          <p className="text-xl mb-8 text-indigo-100">
            Connexion console — gestion de la plateforme et des organisations
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path
                    fillRule="evenodd"
                    d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold">IAM centralisé</h3>
                <p className="text-sm text-indigo-100">Même identifiants que la connexion standard</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold">Rôles étendus</h3>
                <p className="text-sm text-indigo-100">SaaS admin, org admin selon vos droits</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`w-full md:w-1/2 flex items-center justify-center relative ${
          isMobile ? 'p-6 pb-8' : 'p-4 sm:p-6 md:p-8'
        } bg-white md:bg-gray-50`}
      >
        {isMobile && (
          <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none">
            <img
              src="https://images.unsplash.com/photo-1576267423048-15c0040fec78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbGxhYm9yYXRpb24lMjB0ZWFtd29ya3xlbnwwfHx8fDE3NjUzOTY4Nzl8MA&ixlib=rb-4.1.0&q=85"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/30 via-purple-600/30 to-pink-600/30" />
          </div>
        )}
        <div className={`w-full relative z-10 ${isMobile ? 'max-w-sm' : 'max-w-md'}`}>
          <div className="text-center mb-6 sm:mb-8">
            <div
              className={`inline-flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl mb-4 sm:mb-6 shadow-lg ${
                isMobile ? 'w-20 h-20' : 'w-16 h-16 sm:w-20 sm:h-20'
              }`}
            >
              <svg
                className={`text-white ${isMobile ? 'w-10 h-10' : 'w-8 h-8 sm:w-10 sm:h-10'}`}
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden
              >
                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
              </svg>
            </div>
            <h2 className={`font-bold text-gray-900 mb-2 ${isMobile ? 'text-3xl' : 'text-2xl sm:text-3xl'}`}>
              Connexion administrateur
            </h2>
            <p className={`text-gray-600 ${isMobile ? 'text-base' : 'text-sm sm:text-base'}`}>
              Espace équipes et administration Uruganda Platform
            </p>
          </div>

          {error && (
            <div
              className={`bg-red-50 border border-red-200 p-4 rounded-2xl mb-6 ${isMobile ? 'rounded-2xl' : 'rounded-xl'}`}
            >
              <div className="flex items-start gap-3">
                <svg className="h-5 w-5 text-red-500 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className={`text-red-700 ${isMobile ? 'text-base' : 'text-sm'}`}>{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Nom d&apos;utilisateur, e-mail ou téléphone</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={`block w-full pl-12 pr-4 border border-gray-300 bg-white rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition text-base ${
                    isMobile ? 'py-4 min-h-[56px]' : 'py-3'
                  }`}
                  placeholder="Identifiant"
                  required
                  autoFocus
                  autoComplete="username"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Mot de passe</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path
                      fillRule="evenodd"
                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`block w-full pl-12 pr-4 border border-gray-300 bg-white rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition text-base ${
                    isMobile ? 'py-4 min-h-[56px]' : 'py-3'
                  }`}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                isMobile ? 'py-4 min-h-[56px] text-base' : 'py-3'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Connexion en cours...
                </span>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>

          <div className={`text-center pt-6 space-y-3 ${isMobile ? 'pt-8' : ''}`}>
            <p className={`text-gray-600 ${isMobile ? 'text-base' : 'text-sm'}`}>
              Connexion membres / association ?{' '}
              <Link to="/login" className="font-semibold text-indigo-600 hover:text-indigo-700 transition-colors">
                Connexion standard
              </Link>
            </p>
            <p className={`text-gray-500 ${isMobile ? 'text-sm' : 'text-xs'}`}>
              <Link to="/forgot-password" className="text-indigo-600 hover:text-indigo-800">
                Identifiant ou mot de passe oublié
              </Link>
              {' · '}
              <Link to="/register" className="text-indigo-600 hover:text-indigo-800">
                Créer un compte
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginAdmin;
